import React, { useState, useEffect } from 'react';
import {
    Typography,
    Button,
} from '@mui/material';
import styled from 'styled-components';
import { useSnackbar } from 'notistack';

import { IconTrash } from '@tabler/icons-react';

import { FormFilePickerInterface } from '../../Models/Common/FormFieldComponentInterfaces';

import LocalStorageHelper from '../../Utilities/Helpers/LocalStorageHelper';
import GlobalFunctionHelper from '../../Utilities/Helpers/GlobalFunctionHelper';

import { FileSizeType } from '../../Utilities/Enums/FileEnum';
import { ButtonType } from '../../Utilities/Enums/ButtonTypeEnum';

import { ErrorMessage } from '../../Constants/Messages';

import CustomFormTitle from './CustomFormTitle';
import CustomTooltip from './CustomTooltip';
import CustomButton from './CustomButton';

const StyledFormFilePickerfield = styled.div<{ $isError?: boolean; $darkMode?: boolean; $disabled?: boolean; }>`
    width: 100%;

    .filePicker {
        background: ${props => !props?.$disabled ? "transparent" : (props.$darkMode ? '#242728' : '#e9ebec')} !important;
        width: 100%;

        .MuiButtonBase-root.MuiButton-root.MuiButton-contained.MuiButton-containedPrimary.MuiButton-sizeSmall.MuiButton-containedSizeSmall.MuiButton-root.MuiButton-contained.MuiButton-containedPrimary.MuiButton-sizeSmall.MuiButton-containedSizeSmall.uploadFileBtn.filePickerBtn {
            background: transparent;
            padding: 0px !important;
            border-radius: 7px;
            overflow: hidden;
            box-shadow: none;
            border: 1px solid;
            border-color:  ${props => props?.$isError ? "#f44336" : (props.$darkMode ? '#ffffff3b' : '#0000003b')} !important;
            width: 100%;

            .inputFile {
                background: transparent;
                width: 100%;
                height: 38px;
                align-content: center;
                text-align: left;
                color: var(${props => props.$darkMode ? '--darkTextPrimaryColor' : '--textPrimaryColor'}) !important;
                padding: 0px !important;

                &::-webkit-file-upload-button {
                    /*visibility: hidden;*/
                }

                &::file-selector-button {
                    background-color: transparent;
                    height: 38px;
                    border: 0px;
                    border-right: 1px solid;
                    border-color:  ${props => props.$darkMode ? '#ffffff3b' : '#0000003b'} !important;
                    padding: 0px 7px;
                    margin-right: 10px;
                    transition: .5s;
                    color: var(${props => props.$darkMode ? '--darkTextPrimaryColor' : '--textPrimaryColor'}) !important;
                    line-height: 1;
                    font-family: Inter, Helvetica, sans-serif !important;
                    font-size: 12px;
                    font-weight: 600;
                }

                &::-webkit-file-selector-button {
                    background-color: transparent;
                    height: 38px;
                    border: 0px;
                    border-right: 1px solid;
                    border-color:  ${props => props.$darkMode ? '#ffffff3b' : '#0000003b'} !important;
                    padding: 0px 7px;
                    margin-right: 10px;
                    transition: .5s;
                    color: var(${props => props.$darkMode ? '--darkTextPrimaryColor' : '--textPrimaryColor'}) !important;
                    line-height: 1;
                    font-family: Inter, Helvetica, sans-serif !important;
                    font-size: 12px;
                    font-weight: 600;
                }
            }
        }
    }

    .withDeleteBtn {
        .uploadFileBtn.filePickerBtn {
            width: 91.6% !important;
            /*                border: 1px solid #dde1e5;*/
            border-radius: 7px 0px 0px 7px !important;
        }

        .MuiButtonBase-root.MuiButton-root.MuiButton-contained.MuiButton-containedPrimary.MuiButton-sizeMedium.MuiButton-containedSizeMedium.MuiButton-colorPrimary.MuiButton-root.MuiButton-contained.MuiButton-containedPrimary.MuiButton-sizeMedium.MuiButton-containedSizeMedium.MuiButton-colorPrimary {
            width: 40px;
            height: 40px;
            border-radius: 0px 7px 7px 0px !important;
            margin: 0px !important;

            .MuiButton-icon.MuiButton-startIcon.MuiButton-iconSizeMedium {
                margin: 0px !important;
            }
        }
    }

    .subLabelContainer {
        font-size: 12px;
        font-weight: 500;
        color: #878a99 !important;
    }
`;


const FormFilePicker: any = (fieldProps: FormFilePickerInterface) => {
    let isDarkMode = LocalStorageHelper.isDarkMode();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const errorMsg = (fieldProps.errorMessages ? fieldProps.errorMessages[fieldProps.name] : null) ?? null;

    const handleCapture = ({ target }: any) => {
        if (target.files.length > 0) {
            let _proceed = true;
            let _errorMessage = "";
            
            if(fieldProps?.maxFileSize > 0) {
                let _maxFileSizeToByte = GlobalFunctionHelper.convertFileSize(fieldProps?.maxFileSize, fieldProps.fileSizeType, FileSizeType.BYTE);

                if(_maxFileSizeToByte < target.files[0]?.size) {
                    _proceed = false;
                    _errorMessage = `Maximum file size is up to ${fieldProps?.maxFileSize + fieldProps.fileSizeType} only`;
                }
            }

            if(_proceed) {
                const fileReader = new FileReader();

                if(fieldProps.acceptedFileTypes != null && fieldProps.acceptedFileTypes != "" && fieldProps.acceptedFileTypes.length > 0) {
                    _proceed = (target.files[0] && target.files[0].type && target.files[0].type != null && target.files[0].type != "" && fieldProps.acceptedFileTypes.includes(target.files[0].type) ? true : false);
                }

                if(_proceed) {
                    fileReader.readAsDataURL(target.files[0]);
                    fileReader.onload = (e: any) => {
                        if(fieldProps.handleOnChange) {
                            fieldProps.handleOnChange(target.files[0], e?.target?.result ?? null);
                        }
                    };
                } else {
                    _errorMessage = ErrorMessage.INVALID_FILE_FORMAT;
                }
            }

            if(!_proceed) {
                enqueueSnackbar(_errorMessage, { variant: 'error'});
            }
        }
    };

    const renderFilePickerUI = () => {
        let propsData: any = {
            required: fieldProps?.required,
            type: 'file',
            error: (errorMsg && errorMsg != ""),
            id: fieldProps.name,
            name: fieldProps.name,
            disabled: fieldProps.disabled,
            placeholder: (fieldProps.placeholder ? fieldProps.placeholder + (fieldProps.required ? " *" : "") : null),
            className: "inputFile",
            onChange: (e: any) => {
                handleCapture(e);
            },
        };

        let hasDeleteBtn = fieldProps.deleteFile != null;
        let filePickerClassName = "filePicker" + (hasDeleteBtn ? " withDeleteBtn" : "");

        return <div className={filePickerClassName}>
            <Button
            size="small"
            className="uploadFileBtn filePickerBtn"
            variant="contained"
            component="label" >
                <input { ...propsData } />
            </Button>
            {hasDeleteBtn &&
                <CustomTooltip {...{
                    tooltipClass: GlobalFunctionHelper.getRanHex(24),
                    tooltipName: "Remove File",
                    placement: "top",
                    tooltipContent:
                        <CustomButton
                            btnType={ButtonType.DELETE}
                            title=""
                            startIcon={<IconTrash />}
                            onClick={(e: any) => {
                                if(fieldProps.deleteFile) {
                                    fieldProps.deleteFile();
                                }
                            }} />,
                    }} />
            }
        </div>


    }

    return <StyledFormFilePickerfield $isError={errorMsg != null && errorMsg != ""} $darkMode={isDarkMode} $disabled={fieldProps?.disabled ?? false} className={"richTextFieldContainer " + (fieldProps?.fieldClassName != null && fieldProps?.fieldClassName != "" ? fieldProps.fieldClassName : "")}>
        {/* ---------TITLE---------- */}
        {!fieldProps.hideLabel && <CustomFormTitle title={fieldProps.title}
            required={fieldProps.required}
            hideLabel={fieldProps.hideLabel}
            exclamationHoverItemDisplayText={fieldProps.exclamationHoverItemDisplayText} />}
        
        {/* ---------RICHTEXT---------- */}
        {renderFilePickerUI()}

        {/* ---------SIDE CONTENT---------- */}
        {(fieldProps.subLabel && <span className="subLabelContainer">{fieldProps.subLabel}</span>)}
        {errorMsg != null && errorMsg != "" && <Typography className="richTextErrorMessage">{errorMsg}</Typography>}
    </StyledFormFilePickerfield>
}

export default FormFilePicker;